import Box from '@amzn/awsui-components-core/box';
import ColumnLayout from '@amzn/awsui-components-core/column-layout';
import SpaceBetween from '@amzn/awsui-components-core/space-between';
import Token from '@amzn/awsui-components-core/token';
import React, { useCallback, useMemo, useState } from 'react';
import styles from './styles/RowsDimensionPicker.module.scss';

/** One draggable dimension chip: the physical fact column + its display label. */
export interface DimensionItem {
  readonly field: string;
  readonly label: string;
}

interface RowsDimensionPickerProps {
  readonly allDimensions: readonly DimensionItem[];
  readonly selectedDimensions: readonly DimensionItem[];
  readonly onChange: (selected: DimensionItem[]) => void;
  readonly isReadOnly?: boolean;
  readonly selectedLabel?: string;
  readonly selectedDescription?: string;
  readonly emptySelectedText?: string;
  readonly availableDescription?: string;
  /**
   * Prefix for data-testid attributes on the two drop zones and their chips.
   * Chips also expose their field via data-field for direct Playwright selection.
   */
  readonly testIdPrefix?: string;
}

/**
 * Two-pane drag-and-drop dimension picker (ported from xPT's PnlRowsDimensionPicker):
 * drag chips from "Available" into the selected zone to add, drag within the selected
 * zone to reorder (order = grouping hierarchy), drag back to remove.
 */
const RowsDimensionPicker: React.FC<RowsDimensionPickerProps> = ({
  allDimensions,
  selectedDimensions,
  onChange,
  isReadOnly = false,
  selectedLabel = 'Rows',
  selectedDescription = 'Report will show these row dimensions in order',
  emptySelectedText = 'Drag dimensions here to add as rows.',
  availableDescription = 'Drag these to the left to add as rows in your report',
  testIdPrefix
}) => {
  const availableDimensions = useMemo(
    () => allDimensions.filter((d) => !selectedDimensions.some((s) => s.field === d.field)),
    [allDimensions, selectedDimensions]
  );

  const [dragField, setDragField] = useState<string | null>(null);
  const [dragSource, setDragSource] = useState<'selected' | 'available' | null>(null);
  const [overSelected, setOverSelected] = useState(false);
  const [overAvailable, setOverAvailable] = useState(false);

  const resetDrag = useCallback(() => {
    setDragField(null);
    setDragSource(null);
    setOverSelected(false);
    setOverAvailable(false);
  }, []);

  const handleDropOnSelected = useCallback(
    (e: React.DragEvent, insertIndex?: number) => {
      e.preventDefault();
      if (!dragField) return;
      const item = allDimensions.find((d) => d.field === dragField);
      if (!item) {
        resetDrag();
        return;
      }

      const without = selectedDimensions.filter((d) => d.field !== dragField);
      const at = insertIndex ?? without.length;
      const result = [...without.slice(0, at), item, ...without.slice(at)];
      onChange(result);
      resetDrag();
    },
    [dragField, allDimensions, selectedDimensions, onChange, resetDrag]
  );

  const handleDropOnAvailable = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      if (!dragField || dragSource !== 'selected') {
        resetDrag();
        return;
      }
      onChange(selectedDimensions.filter((d) => d.field !== dragField));
      resetDrag();
    },
    [dragField, dragSource, selectedDimensions, onChange, resetDrag]
  );

  const selectedZoneTid = testIdPrefix ? `${testIdPrefix}-selected` : undefined;
  const availableZoneTid = testIdPrefix ? `${testIdPrefix}-available` : undefined;
  const selectedChipTid = testIdPrefix ? `${testIdPrefix}-selected-chip` : undefined;
  const availableChipTid = testIdPrefix ? `${testIdPrefix}-available-chip` : undefined;

  if (isReadOnly) {
    return (
      <SpaceBetween direction="horizontal" size="xs">
        {selectedDimensions.map((d) => (
          <Token key={d.field} label={d.label} />
        ))}
      </SpaceBetween>
    );
  }

  return (
    <ColumnLayout columns={2}>
      <SpaceBetween size="xxs">
        <Box fontWeight="bold" fontSize="body-s">
          {selectedLabel} ({selectedDimensions.length}/{allDimensions.length})
        </Box>
        <Box color="text-body-secondary" fontSize="body-s">
          {selectedDescription}
        </Box>
        <div
          className={`${styles.dropZone} ${overSelected ? styles.dropZoneActive : ''}`}
          onDragOver={(e) => {
            e.preventDefault();
            setOverSelected(true);
          }}
          onDragLeave={() => setOverSelected(false)}
          onDrop={(e) => handleDropOnSelected(e)}
          data-testid={selectedZoneTid}
        >
          {selectedDimensions.length === 0 && (
            <Box color="text-body-secondary" fontSize="body-s">
              {emptySelectedText}
            </Box>
          )}
          {selectedDimensions.map((d, idx) => (
            <span
              key={d.field}
              className={styles.chipSelected}
              draggable
              onDragStart={() => {
                setDragField(d.field);
                setDragSource('selected');
              }}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.stopPropagation();
                handleDropOnSelected(e, idx);
              }}
              onDragEnd={resetDrag}
              style={{ opacity: dragField === d.field ? 0.4 : 1 }}
              data-testid={selectedChipTid}
              data-field={d.field}
            >
              <Token label={d.label} />
            </span>
          ))}
        </div>
      </SpaceBetween>

      <SpaceBetween size="xxs">
        <Box fontWeight="bold" fontSize="body-s">
          Available Dimensions
        </Box>
        <Box color="text-body-secondary" fontSize="body-s">
          {availableDescription}
        </Box>
        <div
          className={`${styles.dropZone} ${overAvailable ? styles.dropZoneActive : ''}`}
          onDragOver={(e) => {
            e.preventDefault();
            setOverAvailable(true);
          }}
          onDragLeave={() => setOverAvailable(false)}
          onDrop={handleDropOnAvailable}
          data-testid={availableZoneTid}
        >
          {availableDimensions.length === 0 && (
            <Box color="text-body-secondary" fontSize="body-s">
              All dimensions are in use.
            </Box>
          )}
          {availableDimensions.map((d) => (
            <span
              key={d.field}
              className={styles.chipAvailable}
              draggable
              onDragStart={() => {
                setDragField(d.field);
                setDragSource('available');
              }}
              onDragEnd={resetDrag}
              style={{ opacity: dragField === d.field ? 0.4 : 1 }}
              data-testid={availableChipTid}
              data-field={d.field}
            >
              <Token label={d.label} />
            </span>
          ))}
        </div>
      </SpaceBetween>
    </ColumnLayout>
  );
};

export default RowsDimensionPicker;
